/**
 * VisitPhotoSetContent.js
 *
 * Content section for visit photo set cards (Instagram-style posts).
 * Displays place name, location, rating, feedback badges, and caption.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../shared/Icon';

const VisitPhotoSetContent = ({ data }) => {
  const { t } = useTranslation('feedCard');
  const { title, description, location, typeData } = data;
  const {
    photoCount = 0,
    rating,
    wouldReturn,
    wouldRecommend,
    city
  } = typeData || {};

  return (
    <div className="flex-grow">
      {/* Place Name */}
      <h3 className="font-semibold text-content-primary line-clamp-2 mb-1">
        {title || t('visitPhotoSetContent.titleFallback')}
      </h3>

      {/* Location + Category + Photo count */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-content-secondary mb-2">
        {(city || location) && (
          <span className="flex items-center">
            <Icon name="mapPin" className="w-3.5 h-3.5 mr-1" />
            <span className="truncate max-w-[180px]">{city || location}</span>
          </span>
        )}
        {!!photoCount && photoCount > 1 && (
          <span className="flex items-center">
            <Icon name="photo" className="w-3.5 h-3.5 mr-1" />
            {t('visitPhotoSetContent.photoCount', { count: photoCount })}
          </span>
        )}
      </div>

      {/* Rating + Quick Feedback */}
      {(!!rating || wouldReturn === 1 || wouldRecommend === 1) && (
        <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
          {!!rating && (
            <span className="flex items-center text-accent">
              {Array.from({ length: 5 }, (_, i) => (
                <Icon key={i} name="star" className={`w-3.5 h-3.5 ${i < rating ? 'text-accent' : 'text-content-muted/30'}`} />
              ))}
            </span>
          )}
          {wouldReturn === 1 && (
            <span className="text-xs bg-success/10 text-success-dark px-2 py-0.5 rounded-full font-medium">
              {t('visitPhotoSetContent.wouldReturn')}
            </span>
          )}
          {wouldRecommend === 1 && (
            <span className="text-xs bg-success/10 text-success-dark px-2 py-0.5 rounded-full font-medium">
              {t('visitPhotoSetContent.wouldRecommend')}
            </span>
          )}
        </div>
      )}

      {/* Caption */}
      {description && (
        <p className="text-sm text-content-secondary line-clamp-3">
          {description}
        </p>
      )}
    </div>
  );
};

export default memo(VisitPhotoSetContent);
